import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share } from "lucide-react";

/**
 * PwaInstallPrompt — small floating "Asenna Veraso" button.
 *
 * Behavior:
 *   • On Chromium browsers we listen for `beforeinstallprompt` and expose our
 *     own branded "Asenna Veraso" button. When the user clicks, we call
 *     `prompt()` on the deferred event.
 *   • On iOS (Safari does not fire the event), we detect the user agent and
 *     show a lightweight "Lisää aloitusnäytölle" info-sheet with steps to
 *     add the PWA manually via the Safari share menu.
 *   • Once the user dismisses the prompt (either successful install or "Ei
 *     nyt"), we remember it in localStorage so we do not nag them.
 *   • When the app is already running as an installed PWA (display-mode is
 *     `standalone` or `window-controls-overlay`), we never render.
 *
 * The component is fully self-contained. Drop it once inside the
 * authenticated shell and it takes care of the rest.
 */

const DISMISS_KEY = "veraso_pwa_install_dismissed_v1";

function isStandalone() {
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    if (window.matchMedia?.("(display-mode: window-controls-overlay)").matches) return true;
    // iOS Safari uses a non-standard property.
    if (window.navigator.standalone === true) return true;
  } catch { /* noop */ }
  return false;
}

function isIos() {
  try {
    const ua = window.navigator.userAgent || "";
    // Only iPhone / iPad / iPod. Modern iPads with iPadOS 13+ report a
    // Mac user-agent — detect via touch API. We deliberately DO NOT flag
    // regular Macs (no touch) so MacBook users don't get the iOS pill.
    const isIosDevice = /iphone|ipad|ipod/i.test(ua);
    const isIpadOs = /macintosh/i.test(ua) && "ontouchend" in document && navigator.maxTouchPoints > 1;
    return isIosDevice || isIpadOs;
  } catch { /* noop */ }
  return false;
}

export default function PwaInstallPrompt() {
  const location = useLocation();
  const routePath = location?.pathname || "/";
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // never show inside the installed app
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS fallback: show our own install pill after a short delay so we do
    // not distract the very first render. On the /kentta field-worker home
    // we appear faster because installing is the key onboarding step there.
    // Re-evaluate on route changes so SPA navigation between /app and /kentta
    // uses the right delay.
    if (isIos()) {
      const isField = window.location.pathname.startsWith("/kentta");
      const t = setTimeout(() => setVisible(true), isField ? 800 : 3500);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBip);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, [routePath]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setIosSheetOpen(false);
  };

  const clickInstall = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        setDeferred(null);
        setVisible(false);
        if (outcome === "accepted") {
          localStorage.setItem(DISMISS_KEY, "1");
        }
      } catch {
        setDeferred(null);
        setVisible(false);
      }
      return;
    }
    // iOS path — open info sheet
    setIosSheetOpen(true);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-slate-200 pl-4 pr-1.5 py-1.5 shadow-2xl shadow-slate-900/10"
        data-testid="pwa-install-prompt"
      >
        <span className="hidden sm:inline text-sm text-slate-700">
          Asenna Veraso puhelimeen
        </span>
        <button
          type="button"
          onClick={clickInstall}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 text-sm transition-all hover:-translate-y-0.5"
          data-testid="pwa-install-btn"
        >
          <Download className="h-4 w-4" /> Asenna
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Sulje asennuskehote"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {iosSheetOpen && <IosInstallSheet onClose={() => setIosSheetOpen(false)} onDismissForever={dismiss} />}
    </>
  );
}

function IosInstallSheet({ onClose, onDismissForever }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="pwa-ios-sheet"
    >
      <div
        className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-7">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold mb-1">iOS · Safari</div>
              <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">Lisää Veraso aloitusnäytölle</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-100 flex-shrink-0"
              aria-label="Sulje"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">
            Applen Safari ei tue asennusta yhdellä painalluksella. Voit lisätä Verason kotinäytöllesi näin:
          </p>
          <ol className="space-y-3.5 mb-6">
            <li className="flex items-start gap-3">
              <div className="h-7 w-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-sm ring-1 ring-emerald-100 flex-shrink-0">1</div>
              <div className="flex-1 pt-0.5">
                <div className="text-sm text-slate-800">Napauta Safarin <span className="font-semibold">jakopainiketta</span> <Share className="inline h-4 w-4 -mt-0.5 text-slate-500" /> ruudun alareunassa.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-7 w-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-sm ring-1 ring-emerald-100 flex-shrink-0">2</div>
              <div className="flex-1 pt-0.5">
                <div className="text-sm text-slate-800">Selaa alas ja valitse <span className="font-semibold">&quot;Lisää Koti-valikkoon&quot;</span>.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-7 w-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-sm ring-1 ring-emerald-100 flex-shrink-0">3</div>
              <div className="flex-1 pt-0.5">
                <div className="text-sm text-slate-800">Napauta <span className="font-semibold">&quot;Lisää&quot;</span> — Veraso ilmestyy aloitusnäytölle omaksi sovelluksekseen.</div>
              </div>
            </li>
          </ol>
          <button
            type="button"
            onClick={onDismissForever}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-700 py-2"
          >
            Älä näytä tätä uudelleen
          </button>
        </div>
      </div>
    </div>
  );
}
